import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
  useRef,
  useContext,
} from 'react';
import { Rect } from 'react-konva';
import { useParams } from 'react-router';
import RMWCanvas from 'components/plateviewer/RMWCanvas';
import Scores from 'components/plateviewer/plate/Scores';

import {
  GetScoreDefn,
  isNullOrUndefined,
  compareWithScoreGroupId,
  GetImagingStatus,
  GetWellsLabels,
  isCommonWellDetails,
  getInspectionIdFromGroupByPlateId,
  showAlertSnackBar,
  isDropModeUrl,
  history,
} from 'utils/Global';
import EScoringGroup from 'enums/EScoringGroup';
import TransformPoints from './TransformPoints';
import {
  AnnotationClientDTO,
  ModalDetails,
  POIClientDTO,
  ROIClientDTO,
  RulerClientDTO,
  ScribbleClientDTO,
  WellDetailsStates,
} from 'business/models/CustomModels';
import {
  CaptureResultTuningValuesDTO,
  LightPathDTO,
  PlateWellDTO,
  StringPropertyDTO,
  WellDrawingDTO,
} from 'business/models/Models';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectAutoSelectNextWell,
  selectCurrentWellScores,
  selectHideAutoScores,
  selectScoreGroup,
  isGenerateRequired,
  selectRegionImaging,
  selectWellDropRegions,
} from 'redux/selectors/StateSelectors';
import {
  callGenerateLayer,
  setAnnotationsDetail,
  setRulersDetail,
  setScribblesDetail,
  setSelectedDrawing,
  setROIDetail,
  setPOIDetail,
  setIsWellImagingEnabled,
  setIsRegionImaging,
  setWellDropRegions,
  setCrystalsDetail,
} from 'redux/reducers/experimentReducer';
import ScaleBarRenderer from './ScaleBarRenderer';
import RefreshScale from './ScaleBar';
import { RMModal } from 'components/modal';
import WellService from 'business/services/WellService';
import { DrawingManagerProps, SelectedDrawingDetails } from './DrawingManagerRenderer';
import WellImage from './WellImage/WellImage';
import {
  selectExp,
  selectIsDrawingMoveToBack,
} from 'redux/selectors/ExperimentStateSelector';
import {
  setCurrentWellScores,
  setDrawingMoveToBack,
  setSelectOverlay,
  setWellNavigationDetails,
} from 'redux/reducers/experimentDataReducer';
import cloneDeep from 'lodash/cloneDeep';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import { EOverlaySelectionType } from 'enums/EOverlaySelectionType';
import { isEqual } from 'lodash';
import DisableImage from '../plate/DisableImage';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  useDropStates,
  useIsWellImagingEnabled,
  useScoreExperimentWells,
} from 'components/Experiment/hooks';
import { CrystalClientDTO, DropUrlParameters } from 'components/Experiment/constants';
import WellCanvasInfoHeader from './wellCanvasInfoHeader';
import {
  useCPNameByCPId,
  useShowOverlays,
  useShowScaleBar,
  useToggleDisableImages,
  useScoresForWells,
  useWellIngredientsAndDropInfo,
  useShowWellDropInfoPanel,
} from 'redux/hooks/experiment';

import { getWellNavigationDetails } from 'components/well/slideShow/utils';
import { TraversalDirection } from 'components/well/slideShow/constants';
import { ImageSizeDetails, WellDisplayMode } from 'components/well/constants';
import { useCanvasPreference, useIsZoomLocked } from 'redux/hooks/common';
import DrawingManager from 'components/well/drawingManager/drawingManager';
import { useDrawingCreationHandlers } from 'components/well/drawingManager/DrawingsCreator';
import {
  useCurrentWellDropImageStatus,
  useIsCreatingNewDrawing,
  useIsCurrentWellImageLoaded,
  useSelectedDrawingTool,
  useSelectedWellDrawing,
  useWellDropImageMetaDataStatus,
} from 'redux/hooks/well';
import { addNewDrawing, updateDrawing } from 'redux/reducers/wellReducer';
import { DrawingType, OverlayTools } from 'components/well/drawingManager/constants';
import { deleteDrawing, saveDrawingThunk } from 'redux/thunks/well';
import drawingSaveManager from 'utils/DrawingSaveManager';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { SizeDetails } from 'components/canvas/constants';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import {
  buildCrystalName,
  getCanvasPointerPosition,
} from 'components/well/drawingManager/utils';
import {
  getImageZoomVisibleArea,
  getMinimumZoomLevel,
  getRegionIdCached,
} from 'components/well/utils';
import { useImageZoomVisibleArea } from 'components/well/hooks';
import WellDropInfoPanel from './wellDropInfoPanel';
import { getWellDropImageUniqueKeyByWellDetails } from 'redux/utils/well';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import { TreeService } from 'business/services/TreeService';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';

const _disputeIcon = RMSvgIconImage({ name: 'DisputeScoreIcon' });
let allInspectionsScores;
let currentWellDisplayed = null;
const wellNumWidth = 100,
  wellNumHeight = 40,
  scaleBarHeight = 40,
  scaleBarGroupWidth = 160;
const scaleBarWidth = scaleBarGroupWidth * 0.35;
let virtualMouse = null;
let lastWellNum = 0;
let zIndexDrawings = [];
const treeService = new TreeService();
export interface Props {
  drawingFetched: boolean;
  setDrawingFetched: (val: boolean) => void;
  lightPaths: LightPathDTO[];
  setLightPaths: (val: LightPathDTO[]) => void;
  wellDetails: WellDetailsStates;
  stageDetails?: any;
  maxImageSizeDetails?: ImageSizeDetails;
  selectedWell?: WellDetailsStates;
  isMultipleDropsRoute?: boolean;
  setStageRef?: (ref, detail, key: string) => void;
  updateWellsImagesSizeDetails?: (val: ImageSizeDetails) => void;
  updateSelectedDetails?: (val: WellDetailsStates) => void;
  onZoomChange?: (
    stage: any,
    oldScale: number,
    scale: number,
    bestFit: boolean,
    wellKey: string,
  ) => void;
  setIsWellLoaded?: (flag: boolean) => void;
  setZoomInvocker?: (onZoom: () => void) => void;
  sizeDetails: SizeDetails;
  imagedWells?: PlateWellDTO[];
  isTraversingVertical?: boolean;
  displayMode?: WellDisplayMode;
  selectedWellsLength?: number;
  isWellInFocus?: boolean;
  wellKey?: string;
  isIndependentZoom?: boolean;
  setWellRef?: (wellKey: string, ref: any) => void;
  minZoomLevel?: number;
  totalWells?: number;
}

const Well = (props: Props) => {
  const {
    setDrawingFetched,
    wellDetails,
    maxImageSizeDetails = undefined,
    selectedWell = undefined,
    isMultipleDropsRoute = false,
    setStageRef = undefined,
    onZoomChange = undefined,
    stageDetails = undefined,
    updateWellsImagesSizeDetails = undefined,
    updateSelectedDetails = undefined,
    setIsWellLoaded,
    setZoomInvocker,
    sizeDetails,
    imagedWells,
    isTraversingVertical = false,
    displayMode,
    selectedWellsLength,
    isWellInFocus,
    wellKey,
    isIndependentZoom,
    setWellRef,
    minZoomLevel = 1,
    totalWells,
  } = props;

  const exp = useRMSelector(selectExp);
  const selectedDrawing = useSelectedWellDrawing();
  const dispatch = useRMDispatch();
  const params = useParams();
  const { annotations, rulers, scribbles, rois, pois, crystals, selectedOverlay } =
    useDropStates(wellDetails.dropNumber);
  const wellDropRegions = useRMSelector(selectWellDropRegions);
  const toggleDisableImages = useToggleDisableImages();
  const isCurrentWellDetailsImageLoaded = useIsCurrentWellImageLoaded(wellDetails);
  const isZoomLocked = useIsZoomLocked();
  const isDropMode = isDropModeUrl();
  const wellDropImageMetaDataStatus = useWellDropImageMetaDataStatus();
  const uKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);
  const imgStatus = useCurrentWellDropImageStatus(uKey);
  const canvasRef = useRef(null);
  const _setSelectedDrawing = useCallback(
    (drawing: SelectedDrawingDetails) => dispatch(setSelectedDrawing(drawing)),
    [dispatch],
  );
  const setAnnotations = useCallback(
    (annotations: AnnotationClientDTO[]) =>
      dispatch(setAnnotationsDetail({ dropNumber: wellDetails.dropNumber, annotations })),
    [dispatch],
  );

  const setROIs = useCallback(
    (rois: ROIClientDTO[]) => {
      dispatch(setROIDetail({ dropNumber: wellDetails.dropNumber, rois }));
    },
    [dispatch, wellDetails.dropNumber],
  );

  const setPOIs = useCallback(
    (pois: POIClientDTO[]) =>
      dispatch(setPOIDetail({ dropNumber: wellDetails.dropNumber, pois })),
    [dispatch],
  );
  const setRulers = useCallback(
    (rulers: RulerClientDTO[]) =>
      dispatch(setRulersDetail({ dropNumber: wellDetails.dropNumber, rulers })),
    [dispatch],
  );
  const setScribbles = useCallback(
    (scribbles: ScribbleClientDTO[]) => {
      dispatch(setScribblesDetail({ dropNumber: wellDetails.dropNumber, scribbles }));
    },
    [dispatch, wellDetails.dropNumber],
  );
  const setCrystals = useCallback(
    (crystals: CrystalClientDTO[]) =>
      dispatch(setCrystalsDetail({ dropNumber: wellDetails.dropNumber, crystals })),
    [dispatch],
  );
  const setIsDrawingMoveToBack = useCallback(
    (val: boolean) => dispatch(setDrawingMoveToBack(val)),
    [dispatch],
  );
  const genCanvas = useCallback(() => dispatch(callGenerateLayer()), [dispatch]);
  const autoSelectNextWell = useRMSelector((state) => selectAutoSelectNextWell(state));
  const scoreWellDrop = useRMSelector((state) => selectCurrentWellScores(state));
  const scoreExperimentWells = useScoreExperimentWells();

  const setSelectedOverlay = useCallback(
    (data) => dispatch(setSelectOverlay(data)),
    [dispatch],
  );
  const _setIsWellImagingEnabled = useCallback(
    (data) => dispatch(setIsWellImagingEnabled(data)),
    [dispatch],
  );
  const _setIsRegionImaging = useCallback(
    (data) => dispatch(setIsRegionImaging(data)),
    [dispatch],
  );
  const hideAutoScores = useRMSelector((state) => selectHideAutoScores(state));
  const scoreGroup = useRMSelector((state) => selectScoreGroup(state));
  const cpName = useCPNameByCPId(wellDetails.cpID);
  const selectedWellDetailsForScores = useMemo(
    () => [
      {
        wellNumber: wellDetails.wellNum,
        plateId: wellDetails.plateID,
        dropNumber: wellDetails.dropNumber,
      },
    ],
    [wellDetails?.wellNum, wellDetails?.plateID, wellDetails?.dropNumber],
  );
  const allWellScores = useScoresForWells(selectedWellDetailsForScores)?.[0];

  const [scores, setScores] = useState<React.ReactNode>();
  const [wellNum, setwellNum] = useState<React.ReactNode>();
  const [wellImaged, setwellImaged] = useState<React.ReactNode>();
  const [wellheader, setWellHeader] = useState<React.ReactNode>();
  const [stage, setStage] = useState(null);
  const [pixelSize, setPixelSize] = useState(0);
  const [dropRegionId, setDropRegionId] = useState(0);
  const [wellImagingEnabled, setWellImagingEnabled] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [isHoverOnWell, setIsHoverOnWell] = useState<boolean>(false);
  const [captureResultTuningValues, setCaptureResultTuningValues] =
    useState<CaptureResultTuningValuesDTO>();
  const [zIndexForMoveBack, setZIndexForMoveBack] = useState(-1);
  const [zIndexForMoveFront, setZIndexForMoveFront] = useState(1);
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    showButtons: true,
  });

  const [imageBatchId, setImageBatchId] = useState(0);
  const [wellScores, setWellScores] = useState([]);
  const [tpObject, setTpObject] = useState<TransformPoints>();
  const [bestFLImageTypeName, setBestFLImageTypeName] = useState<string>('');

  const [imageLoaded, setImageLoaded] = useState(false);
  const isDrawingMoveToBack = useRMSelector(selectIsDrawingMoveToBack);
  const isRegionImaging = useRMSelector(selectRegionImaging);
  const iswellImagingEnabled = useIsWellImagingEnabled(wellDetails?.dropNumber);
  const [showScaleBar] = useShowScaleBar();
  const [showOverlays] = useShowOverlays();
  const [showWellDropInfoPanel] = useShowWellDropInfoPanel();
  const wellIngDropInfo = useWellIngredientsAndDropInfo(
    wellDetails.plateID,
    wellDetails.wellNum,
    wellDetails.dropNumber,
  );
  const drawingTool = useSelectedDrawingTool();
  const expDashboardContext = useContext(ExpDashboardContext);
  const [projectName, setProjectName] = useState('');
  const { plateID, dropNumber } = wellDetails;
  const getCrystalName = (crystalIndex: number) =>
    buildCrystalName({
      template: defaultCrystalName,
      plateID,
      wellNum,
      dropNumber,
      expName,
      projectName,
      index: crystalIndex,
    });
  const drawingCreateEvents = useDrawingCreationHandlers(
    drawingTool,
    wellDetails,
    wellDetails.regionName,
    tpObject,
    () => expDashboardContext.setIsCanvasInFocus(true),
    getCrystalName,
  );
  const allowHotKeyDeleteScore = useCanvasPreference(ECanvasPreferencesKeys.DeleteScore);
  const {
    onMouseDown: drawingOnMouseDown,
    onMouseMove: drawingOnMouseMove,
    onMouseUp: drawingOnMouseUp,
  } = drawingCreateEvents || {};
  const { isCreatingNewDrawing, setIsCreatingNewDrawing } = useIsCreatingNewDrawing();
  const { setImageZoomVisibleArea } = useImageZoomVisibleArea();
  const defaultCrystalName = useSystemProperty<StringPropertyDTO>(
    PropertyConstants.DefaultCrystalName,
    '',
  );
  const { expName, experimentId } = useRMSelector((state) => state.experiment.expData);

  useEffect(() => {
    if (
      !experimentId ||
      !defaultCrystalName.includes('<ProjectName>') ||
      projectName !== ''
    ) {
      return;
    }

    treeService
      .getExpProjectInfo(experimentId)
      .then((projectInfo) => setProjectName(projectInfo.name))
      .catch((e) => console.error('Failed to fetch Project name', e));
  }, [experimentId, defaultCrystalName]);
  useEffect(() => {
    _setIsWellImagingEnabled(wellImagingEnabled);
  }, [wellImagingEnabled]);

  useEffect(() => {
    if (isRegionImaging) {
      _setIsRegionImaging(false);
      setRegionImaging();
    }
  }, [isRegionImaging]);

  const getActiveCanvasScores = useCallback(
    (scores = null) => {
      let tempScores = [];
      const allScores = scores ? scores : allWellScores;
      if (allScores != null || allScores != undefined) {
        const inspection = wellDetails.inspectionSet.groupedInspections.find(
          (insp) => insp.plateId == wellDetails.plateID,
        );

        if (isNullOrUndefined(inspection)) {
          return tempScores;
        }
        const batchId = inspection?.imageBatchId;
        if (isNullOrUndefined(batchId)) {
          return tempScores;
        }
        if (hideAutoScores) {
          tempScores = allScores.filter(
            (ibscores) =>
              ibscores.scoreBatchId === batchId &&
              ibscores.dropNumber === wellDetails.dropNumber &&
              ibscores.isAutoScore === false,
          );
        } else {
          tempScores = allScores.filter(
            (ibscores) =>
              ibscores.scoreBatchId === batchId &&
              ibscores.dropNumber === wellDetails.dropNumber &&
              ibscores.wellNumber === wellDetails.wellNum,
          );
        }
      }

      const autoScoresOfWellDrops = tempScores.filter(
        (score) => score.isAutoScore === true,
      );
      const manualScoresOfWellDrops = tempScores.filter(
        (score) => score.isAutoScore === false,
      );
      const sortedManualScores = manualScoresOfWellDrops.sort(compareWithScoreGroupId);
      const sortedAutoScores = autoScoresOfWellDrops.sort(compareWithScoreGroupId);
      const sortedWellDropScores = [...sortedManualScores, ...sortedAutoScores];
      return sortedWellDropScores;
    },
    [
      allWellScores,
      wellDetails,
      hideAutoScores,
      compareWithScoreGroupId,
      isNullOrUndefined,
    ],
  );

  const applyScoring = useCallback(
    (scoreWell) => {
      allInspectionsScores = scoreWell;
      const tempScores = getActiveCanvasScores();
      setWellScores(tempScores);
    },
    [allInspectionsScores, getActiveCanvasScores],
  );

  useEffect(() => {
    if (
      !isNullOrUndefined(scoreWellDrop) &&
      !(lastWellNum === null || lastWellNum !== wellDetails.wellNum)
    ) {
      applyScoring(scoreWellDrop);
    }
  }, [scoreWellDrop]);

  useEffect(() => {
    const scale = stage ? stage.scaleX() : 1;
    const x = stage ? stage.x() : 1;
    const y = stage ? stage.y() : 1;
    renderScores(x, y, scale);
  }, [wellScores]);

  useEffect(() => {
    const scale = stage ? stage.scaleX() : 1;
    const x = stage ? stage.x() : 1;
    const y = stage ? stage.y() : 1;
    setwellImageOnDrop(x, y, scale);
  }, [iswellImagingEnabled, tpObject]);

  useEffect(() => {
    const scale = stage ? stage.scaleX() : 1;
    const x = stage ? stage.x() : 1;
    const y = stage ? stage.y() : 1;
    setWellInfoDetails(x, y, scale);
    setwellImageOnDrop(x, y, scale);
    setZoomScale(scale);
  }, [
    tpObject,
    bestFLImageTypeName,
    stageDetails,
    stage?.attrs.scaleX,
    wellDetails,
    showScaleBar,
  ]);

  useEffect(() => {
    if (selectedDrawing && isDrawingMoveToBack) {
      moveTo(true);
      setIsDrawingMoveToBack(false);
    }
  }, [isDrawingMoveToBack]);

  const setCanvasRef = (instance) => {
    canvasRef.current = instance;
    setWellRef(wellKey, instance);
  };

  const _setWellNum = useCallback(
    (x: number, y: number, scale: number) => {
      const { dropNumber } = wellDetails;
      if (!isNullOrUndefined(GetWellsLabels()[wellDetails.wellNum - 1])) {
        setwellNum(GetWellsLabels()[wellDetails.wellNum - 1].name + '-' + dropNumber);
      }
    },
    [wellDetails, wellNumWidth, wellNumHeight, GetWellsLabels],
  );

  const _setWellHeader = useCallback(
    (x: number, y: number, scale: number) => {
      const details = wellDetails as WellDetailsStates;
      const inspectionName = details.inspectionSet.name.replace('\r\n', '');
      const flDetail = bestFLImageTypeName !== '' ? ' - ' + bestFLImageTypeName : null;
      setWellHeader(
        'Drop ' +
          details.dropNumber +
          ' - ' +
          details.regionName +
          ' - ' +
          cpName +
          ' - ' +
          inspectionName +
          (!flDetail ? '' : flDetail),
      );
    },
    [wellDetails, cpName, wellNumHeight, bestFLImageTypeName, setWellHeader],
  );

  const setWellInfoDetails = useCallback(
    (x: number, y: number, scale: number) => {
      _setWellNum(x, y, scale);
      _setWellHeader(x, y, scale);
    },
    [_setWellNum, _setWellHeader],
  );

  const setwellImageOnDrop = (x: number, y: number, scale: number) => {
    if (iswellImagingEnabled) {
      setwellImaged(null);
      return;
    }
    if (iswellImagingEnabled === false) {
      const wellImaged = <DisableImage x={0} y={0} width={24} height={24} />;
      setwellImaged(wellImaged);
    }
  };

  const fetchDrawing = useCallback(async () => {
    const dropRegionID = getRegionIdCached(
      wellDetails.plateID || params.plateID,
      wellDetails.wellNum || params.wellNum,
      wellDetails.dropNumber || params.dropNumber,
      wellDetails.regionName || params.regionName,
      exp.regions,
    );
    if (!dropRegionID) {
      return;
    }
    setDropRegionId(dropRegionID);
    const { cpID, dropNumber, regionName } = wellDetails;
    const imageBatchId = getInspectionIdFromGroupByPlateId(
      wellDetails,
      wellDetails.plateID,
    );
    const wellService = new WellService();
    const overlays = wellService.getWellDrawing(
      wellDetails.plateID || params.plateID,
      wellDetails.wellNum || params.wellNum,
      imageBatchId || params.imageBatchId,
      dropNumber || params.dropNumber,
      cpID || params.cpID,
      regionName || params.regionName,
    );
    return overlays;
  }, [wellDetails, params, exp.regions, setDropRegionId]);

  const setZindexForDrawing = (zIndex) => {
    zIndexDrawings = zIndexDrawings?.filter(
      (x) =>
        !(x.id === selectedDrawing.drawingId && x.type === selectedDrawing.drawingType),
    );
    zIndexDrawings.push({
      type: selectedDrawing.drawingType,
      id: selectedDrawing.drawingId,
      zIndex: zIndex,
    });
  };

  const updateWellOverLays = useCallback(async () => {
    const well: WellDrawingDTO = await fetchDrawing();
    if (well) {
      setWellImagingEnabled(well.imagingEnabled);
      setCaptureResultTuningValues(well.captureResultTuningValuesDto);
    }
  }, [
    allInspectionsScores,
    fetchDrawing,
    dispatch,
    setCurrentWellScores,
    getActiveCanvasScores,
    setWellScores,
    setScores,
    setWellImagingEnabled,
    setCaptureResultTuningValues,
  ]);

  const fetchData = useCallback(async () => {
    updateWellOverLays();
    setDrawingFetched(true);
  }, [updateWellOverLays]);

  useEffect(() => {
    const { inspectionSet } = wellDetails;
    let imgBatchId = parseInt(params.imageBatchId);
    if (!isNullOrUndefined(inspectionSet)) {
      const plateInspection = inspectionSet.groupedInspections.find(
        (insp) => insp.plateId == wellDetails.plateID,
      );
      if (plateInspection != undefined) {
        imgBatchId = plateInspection.imageBatchId;
      }
    }
    setImageBatchId(imgBatchId);
    if (imageLoaded && imgBatchId) {
      fetchData();
    }
    lastWellNum = wellDetails.wellNum;
  }, [wellDetails, imageLoaded]);

  useEffect(() => {
    const _minZoomLevel = getMinimumZoomLevel(
      isIndependentZoom,
      totalWells,
      minZoomLevel,
    );
    if (_minZoomLevel > 1 && canvasRef) {
      canvasRef?.current?.ZoomHandler(_minZoomLevel);
    }
  }, [wellDetails?.wellNum, wellDetails?.plateID, canvasRef]);

  useEffect(() => {
    if (!isMultipleDropsRoute || lastWellNum !== wellDetails.wellNum) {
      cleanStates();
    }
  }, [wellDetails]);

  useEffect(() => {
    if (allWellScores?.length > 0) {
      dispatch(setCurrentWellScores(allWellScores));
      const tempScores = getActiveCanvasScores();
      setWellScores(tempScores);
    } else {
      if (scores && !allWellScores?.length) {
        setScores(null);
      }
    }
  }, [allWellScores, hideAutoScores, wellDetails, wellDetails.dropNumber, scoreGroup]);

  const renderScores = useCallback(
    (x: number, y: number, scale: number) => {
      let tempScores = [];
      if (scoreGroup === EScoringGroup.All) {
        tempScores = wellScores;
      } else if (scoreGroup === EScoringGroup.Auto) {
        tempScores = wellScores.filter((fscores) => fscores.isAutoScore == true);
      } else if (scoreGroup === EScoringGroup.Manual) {
        tempScores = wellScores.filter((fscores) => fscores.isAutoScore == false);
      } else {
        tempScores = wellScores.filter(
          (fscores) => fscores.wellDropScoreGroupId === scoreGroup,
        );
      }
      const scoresComponent = (
        <Scores
          scoresOfWellDrops={tempScores}
          width={24}
          height={24}
          x={0}
          y={0}
          scale={0}
          icon={_disputeIcon}
          wellView={true}
          sizeDetails={sizeDetails}
        />
      );
      setScores(scoresComponent);
    },
    [wellScores],
  );

  useLayoutEffect(() => {
    if (
      setIsWellLoaded &&
      (!isMultipleDropsRoute || isEqual(selectedWell, wellDetails))
    ) {
      if (wellDetails && isCurrentWellDetailsImageLoaded) {
        setIsWellLoaded(true);
      } else {
        setIsWellLoaded(false);
      }
    }
  }, [wellDetails, isCurrentWellDetailsImageLoaded, selectedWell]);

  const moveToCanvasView = () => {
    history.push(`/exp/${exp?.experimentId}`);
  };

  const restartWellNavigationOnceEnd = useCanvasPreference(
    ECanvasPreferencesKeys.RestartWellNavigationOnceEnd,
  );

  const showEndOfExperimentMessage = (
    wellDetails: WellDetailsStates,
    imagedWells: PlateWellDTO[],
    backIndex: number = 2,
  ) => {
    if (
      imagedWells.length &&
      wellDetails.plateID === imagedWells[imagedWells.length - backIndex]?.plateId &&
      wellDetails.wellNum === imagedWells[imagedWells.length - backIndex]?.wellNumber
    ) {
      showAlertSnackBar(
        EAlertType.INFO,
        ESnackBarPosition.TOP,
        "You've reached the end of the current Experiment.",
      );
    }
  };

  const getAutoSelectNextWellNumToScore = () => {
    if (autoSelectNextWell) {
      const nextWellDetails = getWellNavigationDetails(
        exp?.plateDto,
        wellDetails,
        1,
        TraversalDirection.Row,
        imagedWells,
      );
      return nextWellDetails;
    }
    return null;
  };

  const onKeyPress = (e: KeyboardEvent) => {
    /**
     * If the modal is opened, key navigation should be paused.
     */
    if (modalDetails.showModal || !isWellInFocus) {
      return;
    }
    const { key, keyCode } = e;
    const ctrlKey = e.ctrlKey || e.metaKey;
    if (
      keyCode === 46 &&
      (isMultipleDropsRoute ? isEqual(selectedWell, wellDetails) : true) &&
      selectedDrawing
    ) {
      dispatch(deleteDrawing(selectedDrawing));
      if (stage?.container) {
        const container = stage.container();
        container.className = 'default';
      }
    } else if (
      ((keyCode > 47 && keyCode < 58) ||
        (keyCode > 64 && keyCode < 91) ||
        (keyCode > 95 && keyCode < 123)) &&
      (isMultipleDropsRoute ? isEqual(selectedWell, wellDetails) : true)
    ) {
      if (!GetImagingStatus()) {
        return;
      }
      if (isNullOrUndefined(GetScoreDefn())) {
        return;
      }
      if (!ctrlKey && !e.altKey && !e.shiftKey) {
        const score = GetScoreDefn().find((s) => s.hotKey === key);
        if (isNullOrUndefined(score)) {
          return;
        }
        scoreExperimentWells({
          wellDropScoreId: score.wellDropScoreId,
          wellDropScoreGroupId: score.wellDropScoreGroupId,
          disAllowScoreRemove: !allowHotKeyDeleteScore,
          autoSelectNextWell: autoSelectNextWell,
          nextWellDetails: getAutoSelectNextWellNumToScore(),
        });
        showEndOfExperimentMessage(wellDetails, imagedWells);
      }
    } else if (!ctrlKey && !e.altKey && !e.shiftKey && keyCode >= 37 && keyCode <= 40) {
      const nextWellDetails = getWellNavigationDetails(
        exp?.plateDto,
        wellDetails,
        keyCode === 39 || keyCode === 40 ? 1 : -1,
        keyCode === 38 || keyCode === 40
          ? TraversalDirection.Column
          : TraversalDirection.Row,
        imagedWells,
      );

      showEndOfExperimentMessage(wellDetails, imagedWells);
      if (
        nextWellDetails.plateID === imagedWells[0]?.plateId &&
        nextWellDetails.wellNum === imagedWells[0]?.wellNumber &&
        !restartWellNavigationOnceEnd
      ) {
        return;
      }
      setSelectedOverlay(EOverlaySelectionType.POINTER);
      const parts = window.location.pathname.split('/');
      parts[DropUrlParameters.PLATEID] = nextWellDetails.plateID.toString();
      parts[DropUrlParameters.WELLNUM] = nextWellDetails.wellNum.toString();
      history.replace(parts.join('/'));
      dispatch(setWellNavigationDetails([nextWellDetails]));
    } else if (keyCode === 13) {
      moveToCanvasView();
    }
  };

  const cleanStates = useCallback(() => {
    lastWellNum = 0;
    setRulers([]);
    setScribbles([]);
    setPOIs([]);
    setROIs([]);
    setAnnotations([]);
    setScores([]);
    setBestFLImageTypeName('');
    setCaptureResultTuningValues(null);
  }, [
    lastWellNum,
    setRulers,
    setScribbles,
    setPOIs,
    setROIs,
    setAnnotations,
    setScores,
    setBestFLImageTypeName,
    setCaptureResultTuningValues,
  ]);

  const onMouseDown = (e) => {
    if (drawingOnMouseDown && tpObject) {
      const drawingDetails = drawingOnMouseDown(e);
      setIsCreatingNewDrawing(true);
      if (drawingDetails) {
        const { drawing, type } = drawingDetails;
        dispatch(
          addNewDrawing({
            plateId: wellDetails.plateID,
            wellNumber: wellDetails.wellNum,
            dropNumber: wellDetails.dropNumber,
            regionName: wellDetails.regionName,
            drawingType: type,
            drawingUpdateDetails: drawing,
          }),
        );
      }
    }
  };

  const onMouseMove = (e) => {
    currentWellDisplayed = wellDetails;
    const stage = e.target.getStage();
    if (!stage) {
      return;
    }
    const { x, y } = getCanvasPointerPosition(e);
    const { width, height } = stage.attrs;
    if ((x <= 2 || x >= width - 2 || y <= 2 || y >= height - 2) && isCreatingNewDrawing) {
      onMouseUp(e);
      return;
    }
    setVirtualMouseDetails(stage);
    if (drawingTool === OverlayTools.POINTER) {
      changeDrawingIcon(e, 'default');
    } else {
      changeDrawingIcon(e, 'crosshair');
    }
    if (drawingOnMouseMove && isCreatingNewDrawing && tpObject) {
      const drawingDetails = drawingOnMouseMove(e);
      if (drawingDetails) {
        const { drawing, type, isDrawingFirstClick } = drawingDetails;
        if (isDrawingFirstClick) {
          dispatch(
            addNewDrawing({
              plateId: wellDetails.plateID,
              wellNumber: wellDetails.wellNum,
              dropNumber: wellDetails.dropNumber,
              regionName: wellDetails.regionName,
              drawingType: type,
              drawingUpdateDetails: drawing,
            }),
          );
        } else {
          dispatch(
            updateDrawing({
              plateId: wellDetails.plateID,
              wellNumber: wellDetails.wellNum,
              dropNumber: wellDetails.dropNumber,
              regionName: wellDetails.regionName,
              drawingType: type,
              id: drawing.id,
              drawingUpdateDetails: drawing,
            }),
          );
        }
      }
    }
  };

  const onMouseUp = async (e) => {
    if (drawingOnMouseUp && isCreatingNewDrawing) {
      setIsCreatingNewDrawing(false);
      const drawingDetails = await drawingOnMouseUp(e);
      if (drawingDetails) {
        const { drawing, type } = drawingDetails;
        const savePromise = dispatch(
          saveDrawingThunk({
            plateId: wellDetails.plateID,
            wellNumber: wellDetails.wellNum,
            dropNumber: wellDetails.dropNumber,
            cpId: wellDetails.cpID,
            imageBatchId,
            regionName: wellDetails.regionName,
            drawingType: type,
            drawingUpdateDetails: drawing,
            tpObject,
          }),
        );
        drawingSaveManager.save(drawing.id, savePromise);
        if (!showOverlays) {
          showAlertSnackBar(
            EAlertType.INFO,
            ESnackBarPosition.TOP,
            'Drawings are hidden',
          );
        }
      }
    } else {
      setIsCreatingNewDrawing(false);
    }
  };

  const changeDrawingIcon = useCallback(
    (e, icon) => {
      const stage = e.target.getStage();
      if (isNullOrUndefined(stage)) {
        return;
      }
      const container = stage.container();
      if (container.style.cursor === icon) {
        return;
      }
      container.style.cursor = icon;
    },
    [isNullOrUndefined],
  );

  const setVirtualMouseDetails = useCallback(
    (stageRef) => {
      const pos = stageRef.getPointerPosition();
      if (displayMode === WellDisplayMode.Compare && pos && stageRef) {
        virtualMouse = (
          <Rect
            x={(pos.x - stageRef.x()) / stageRef.scaleX()}
            y={(pos.y - stageRef.y()) / stageRef.scaleX()}
            width={isIndependentZoom ? 8 : 8 / stageRef.scaleX()}
            height={isIndependentZoom ? 8 : 8 / stageRef.scaleX()}
            stroke={'white'}
            fill={'black'}
            strokeWidth={1 / stageRef.scaleX()}
          />
        );
        genCanvas();
      }
    },
    [displayMode, virtualMouse, genCanvas],
  );

  const setZoomValues = (scale) => {
    if (!stage) {
      return;
    }
    setImageZoomVisibleArea(getImageZoomVisibleArea(stage, tpObject, scale));
  };

  const onZoomChangeEvent = useCallback(
    (stage, scale, newScale, bestFit) => {
      setwellImageOnDrop(stage.x(), stage.y(), scale);
      setVirtualMouseDetails(stage);
      setZoomScale(scale);
      setZoomValues(scale);
      if (onZoomChange) {
        onZoomChange(stage, zoomScale, newScale, bestFit, wellKey);
      }
    },
    [setVirtualMouseDetails, setwellImageOnDrop, setZoomScale, onZoomChange],
  );

  const onMouseEnter = useCallback(
    (e) => {
      if (drawingTool === OverlayTools.POINTER) {
        return;
      }
      if (selectedOverlay) {
        changeDrawingIcon(e, 'crosshair');
      }
    },
    [drawingTool, selectedOverlay, changeDrawingIcon],
  );

  const onMouseLeave = useCallback(
    (e) => {
      onMouseUp(e);
      changeDrawingIcon(e, 'default');
    },
    [changeDrawingIcon, isCreatingNewDrawing],
  );

  const onClick = useCallback(() => {
    if (
      updateSelectedDetails &&
      wellDetails !== selectedWell &&
      isMultipleDropsRoute &&
      drawingTool === OverlayTools.POINTER
    ) {
      updateSelectedDetails(wellDetails);
    }
  }, [
    wellDetails,
    selectedWell,
    isMultipleDropsRoute,
    drawingTool,
    updateSelectedDetails,
  ]);
  const stageRef = useCallback(
    (stage) => {
      setStage(stage);
      if (setStageRef) {
        setStageRef(stage, wellDetails, wellKey);
      }
    },
    [setStage, setStageRef, wellDetails],
  );

  const moveTo = useCallback(
    (back = true) => {
      if (selectedDrawing.drawingType === DrawingType.Scribble) {
        const updatedScribbles = scribbles.map((s) => {
          if (s.scribbleStartPointsXYofWell.id == selectedDrawing.drawingId) {
            s.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(s.zIndex);
          }
          return s;
        });

        setScribbles([...updatedScribbles]);
      } else if (selectedDrawing.drawingType === DrawingType.Ruler) {
        const tempRulers = cloneDeep(rulers);
        const updatedRulers = tempRulers.map((r) => {
          if (r.id == selectedDrawing.drawingId) {
            r.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(r.zIndex);
          }
          return r;
        });
        setRulers([...updatedRulers]);
      } else if (selectedDrawing.drawingType === DrawingType.ROI) {
        // ROI Condition
        const tempROIs = cloneDeep(rois);
        const updatedROIs = tempROIs.map((r) => {
          if (r.id == selectedDrawing.drawingId) {
            r.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(r.zIndex);
          }
          return r;
        });
        setROIs([...updatedROIs]);
      } else if (selectedDrawing.drawingType === DrawingType.POI) {
        // POI Condition
        const tempPOIs = cloneDeep(pois) || [];
        const updatedPOIs = tempPOIs.map((p) => {
          if (p.id == selectedDrawing.drawingId) {
            p.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(p.zIndex);
          }
          return p;
        });

        setPOIs([...updatedPOIs]);
      } else if (selectedDrawing.drawingType === DrawingType.Annotation) {
        // POI Condition
        const updatedAnnotations = annotations.map((a) => {
          if (a.id == selectedDrawing.drawingId) {
            a.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(a.zIndex);
          }
          return a;
        });

        setAnnotations([...updatedAnnotations]);
      } else if (selectedDrawing.drawingType === DrawingType.CRYSTAL) {
        const tempCrystals = cloneDeep(crystals);
        const updatedCrystals = tempCrystals.map((crystal) => {
          if (crystal.id == selectedDrawing.drawingId) {
            crystal.zIndex = back ? zIndexForMoveBack : zIndexForMoveFront;
            setZindexForDrawing(crystal.zIndex);
          }
          return crystal;
        });

        setCrystals([...updatedCrystals]);
      }

      back
        ? setZIndexForMoveBack(zIndexForMoveBack - 1)
        : setZIndexForMoveFront(zIndexForMoveFront + 1);

      if (back) {
        _setSelectedDrawing(null);
      }
    },
    [
      selectedDrawing,
      scribbles,
      rois,
      pois,
      rulers,
      annotations,
      crystals,
      zIndexForMoveBack,
      zIndexForMoveFront,
      _setSelectedDrawing,
    ],
  );

  const setRegionImaging = useCallback(async () => {
    const id = selectedDrawing ? selectedDrawing.drawingId : dropRegionId;
    if (!wellImagingEnabled && id !== dropRegionId) {
      setModalDetails({
        showModal: true,
        setShowModal: () => setModalDetails({ ...modalDetails, showModal: false }),
        onOk: () => setModalDetails({ ...modalDetails, showModal: false }),
        body: (
          <div id="well-modal-warning-text">
            You must enable well drop imaging in order to change the imaging state of a
            region.
          </div>
        ),
        isOkOnly: true,
        heading: 'Warning',
        okButtonText: 'OK',
        showButtons: true,
      });
      return;
    }
    const wellinfo = new WellService();

    if (id === dropRegionId) {
      if (isNullOrUndefined(wellImagingEnabled)) {
        return;
      }
      await wellinfo.disableImaging([id], !wellImagingEnabled);
      setWellImagingEnabled(!wellImagingEnabled);
      if (wellDropRegions?.length > 0) {
        const tempWellDropRegions = cloneDeep(wellDropRegions);
        tempWellDropRegions.find((x) => x.regionId === dropRegionId).imagingEnabled =
          !wellImagingEnabled;
        dispatch(setWellDropRegions(tempWellDropRegions));
        toggleDisableImages([dropRegionId], !wellImagingEnabled);
      }
    } else {
      const region = rois.find((r) => r.id === id);
      const navigatorMap: boolean = await wellinfo.disableImaging(
        [region.id],
        !region.imagingEnabled,
      );
      if (!navigatorMap) {
        return;
      }
      const updatedRoi = [...rois];
      updatedRoi.map((r) => {
        if (r.id === id) {
          r.imagingEnabled = !r.imagingEnabled;
        }
      });
      setROIs([...updatedRoi]);
    }
  }, [
    rois,
    selectedDrawing,
    dropRegionId,
    wellImagingEnabled,
    modalDetails,
    setWellImagingEnabled,
    setModalDetails,
    setROIs,
  ]);

  const unselectDrawing = (e) => {
    _setSelectedDrawing(null);
  };

  const wellImage = useMemo(() => {
    return (
      <WellImage
        sizeDetails={sizeDetails}
        tpObject={tpObject}
        wellDetails={wellDetails}
        selectedWell={selectedWell}
        isMultipleDropsRoute={isMultipleDropsRoute}
        imageSizeDetails={maxImageSizeDetails}
        captureResultTuningValues={captureResultTuningValues}
        setPixelSize={setPixelSize}
        setBestFLImageTypeName={setBestFLImageTypeName}
        updateWellsImagesSizeDetails={updateWellsImagesSizeDetails}
        setTpObject={setTpObject}
        setImageLoaded={setImageLoaded}
        params={params}
        isTraversingVertical={isTraversingVertical}
      />
    );
  }, [
    tpObject,
    wellDetails,
    selectedWell,
    isMultipleDropsRoute,
    maxImageSizeDetails,
    captureResultTuningValues,
    setPixelSize,
    setBestFLImageTypeName,
    updateWellsImagesSizeDetails,
    setTpObject,
    setImageLoaded,
    sizeDetails.canvasWidth,
    wellDropImageMetaDataStatus,
  ]);

  const scaleDetails = useMemo(() => {
    return RefreshScale(pixelSize, zoomScale, scaleBarWidth / zoomScale);
  }, [pixelSize, zoomScale]);

  if (wellDetails === undefined || wellDetails === null) {
    return (
      <div
        id="well-scale-detail-warning-text"
        style={{ textAlignLast: 'center', width: '100%' }}
      >
        {' '}
        Select a well from canvas{' '}
      </div>
    );
  }
  //todo: uplift to common parent of well/drop/compare
  if (wellDropImageMetaDataStatus !== EFetchingStatus.Loaded) {
    return (
      <div
        id="well-loading-meta-data-status"
        style={{
          width: sizeDetails.canvasWidth,
          height: sizeDetails.canvasHeight,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        Loading meta-data...
      </div>
    );
  }
  return (
    <div
      id="well-main-container"
      style={{ display: 'flex', flexDirection: 'column' }}
      onMouseEnter={() => setIsHoverOnWell(true)}
      onMouseLeave={() => setIsHoverOnWell(false)}
    >
      <WellCanvasInfoHeader
        wellKey={wellKey}
        canvasWidth={sizeDetails.canvasWidth}
        wellScores={scores}
        wellNumber={wellNum as string}
        wellInfo={wellheader as string}
        disableImagingIcon={wellImaged}
        selectedWellsLength={selectedWellsLength}
        isIndependentZoom={isIndependentZoom}
      />
      <RMWCanvas
        ref={setCanvasRef}
        key={wellKey}
        canvasStageId={`canvas-${wellKey}`}
        canvasStageContainerId={`canvas-container-${wellKey}`}
        width={sizeDetails.canvasWidth}
        height={sizeDetails.canvasHeight}
        canvasWidth={sizeDetails.canvasWidth}
        canvasHeight={sizeDetails.canvasHeight}
        stageRef={stageRef}
        onClick={onClick}
        onKeyPress={onKeyPress}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onZoomChange={onZoomChangeEvent}
        setZoomInvocker={setZoomInvocker}
        draggable={true}
        cols={1}
        rows={1}
        isDrawingActive={isCreatingNewDrawing}
        stage={stage}
        showScoresToolTip={isHoverOnWell}
        isZoomLocked={isZoomLocked && isDropMode}
        minZoomLevel={getMinimumZoomLevel(isIndependentZoom, totalWells, minZoomLevel)}
        fixedContent={
          showScaleBar && (
            <ScaleBarRenderer
              x={0}
              y={sizeDetails.canvasHeight - scaleBarHeight}
              width={scaleBarGroupWidth}
              height={scaleBarHeight}
              scaleBarWidth={scaleBarWidth}
              scaleBarHeight={6}
              fontSize={15}
              text={scaleDetails.Text}
              scale={1}
            />
          )
        }
      >
        {wellImage}
        <Rect
          id="well-drawing-section"
          x={0}
          y={0}
          width={sizeDetails.canvasWidth}
          height={sizeDetails.canvasHeight}
          onClick={unselectDrawing}
        />
        {isCurrentWellDetailsImageLoaded && (
          <DrawingManager
            wellDetails={wellDetails}
            tpObject={tpObject}
            canvasHeight={sizeDetails.canvasHeight}
            canvasWidth={sizeDetails.canvasWidth}
            zoomScale={zoomScale}
            regionName={wellDetails.regionName}
            hideDrawings={!showOverlays}
          />
        )}
        {showWellDropInfoPanel &&
          wellIngDropInfo &&
          tpObject &&
          imgStatus?.fetchingStatus === EFetchingStatus.Loaded && (
            <WellDropInfoPanel wellIngDropInfo={wellIngDropInfo} tpObject={tpObject} />
          )}
        <VirtualMouse wellDetails={wellDetails} displayMode={displayMode} />
      </RMWCanvas>
      <RMModal
        id="well-modal-section"
        showModal={modalDetails.showModal}
        setShowModal={modalDetails.setShowModal}
        onOk={modalDetails.onOk}
        body={modalDetails.body}
        isOkOnly={modalDetails.isOkOnly}
        heading={modalDetails.heading}
        okButtonText={modalDetails.okButtonText}
        showButtons={modalDetails.showButtons}
        dialogClassName={modalDetails.heading === 'Save Image' ? 'modal-fullw' : null}
        okayDisabled={false}
      />
    </div>
  );
};
export default Well;

export type WellDrawingRenderer = {
  scribbles: any[];
  annotations: any[];
  poi: any[];
  roi: any[];
  ruler: any[];
};

export enum EDrawingType {
  Scribble,
  Ruler,
  ROI,
  POI,
  Annotation,
  CRYSTAL,
}

export const WellDrawingContext = React.createContext<DrawingManagerProps>({
  tpObject: null,
  scribbles: [],
  rulers: [],
  pois: [],
  rois: [],
  crystals: [],
  lightPaths: [],
  zoomScale: 1,
  wellDetail: null,
  drawingTool: '',
  lockedIcon: null,
  zIndexForMoveFront: 1,
  setLightPaths: (val) => {},
  setZIndexForMoveFront: (val) => {},
  setPOIs: (val) => {},
  setROIs: (val) => {},
  setRulers: (val) => {},
  setScribbles: (val) => {},
  setCrystals: (val) => {},
  doubleClickAnnotation: () => {},
});

const VirtualMouse = ({ wellDetails, displayMode }: any) => {
  const genCanvas = useRMSelector(isGenerateRequired);
  useEffect(() => {}, [genCanvas]);
  return !isCommonWellDetails(currentWellDisplayed, wellDetails) &&
    displayMode === WellDisplayMode.Compare
    ? virtualMouse
    : null;
};
